import { useState } from 'react';
import { exchangeRatesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';

/** Currencies the platform can hold a rate for (GBP is the base, so not listed). */
const CURRENCIES = ['PLN', 'EUR', 'USD', 'AUD', 'CAD', 'CHF', 'CNY', 'INR', 'JPY', 'MXN'];

interface AddExchangeRateFormProps {
  /** Called after a rate is stored, so the page can reload. */
  onSaved: () => void;
}

/**
 * Lets an admin enter a rate the ERP does not supply (e.g. PLN), or correct one.
 *
 * The rate is typed the platform's way round — 1 GBP = X — and the inverse is
 * shown next to it, so a rate typed backwards is visible before it is saved.
 *
 * @param onSaved - reload hook for the parent page
 * @returns the form card
 */
export default function AddExchangeRateForm({ onSaved }: AddExchangeRateFormProps) {
  const [currency, setCurrency] = useState('PLN');
  const [rate, setRate] = useState('');
  const [rateDate, setRateDate] = useState('');
  const [saving, setSaving] = useState(false);

  const value = parseFloat(rate);
  const valid = Number.isFinite(value) && value > 0 && rateDate !== '';

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await exchangeRatesAPI.addManualRate({ currency, rate, rate_date: rateDate });
      toast.success(`Saved: 1 GBP = ${rate} ${currency} from ${rateDate}`);
      setRate('');
      onSaved();
    } catch (err: unknown) {
      // Field errors arrive as details.fields.{field}: [message]; show the first.
      const data = (err as { response?: { data?: { message?: string; details?: { fields?: Record<string, string[]> } } } }).response?.data;
      const fieldError = data?.details?.fields ? Object.values(data.details.fields).flat()[0] : undefined;
      toast.error(fieldError || data?.message || 'Could not save the rate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Add a rate</CardTitle>
        <p className="text-sm text-muted-foreground">
          For currencies the ERP does not send, or to correct one. Replaces any rate already stored for that date.
        </p>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label htmlFor="fx-currency">Currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger id="fx-currency" className="w-[110px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="fx-rate">1 GBP =</Label>
          <Input id="fx-rate" inputMode="decimal" placeholder="e.g. 5.0613" value={rate}
            onChange={(e) => setRate(e.target.value)} className="w-36" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="fx-date">Effective from</Label>
          <Input id="fx-date" type="date" value={rateDate} onChange={(e) => setRateDate(e.target.value)} className="w-44" />
        </div>
        <div className="text-sm text-muted-foreground pb-2 min-w-[12rem]">
          {Number.isFinite(value) && value > 0 ? `1 ${currency} = ${(1 / value).toFixed(6)} GBP` : ''}
        </div>
        <Button onClick={save} disabled={!valid || saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
          Save rate
        </Button>
      </CardContent>
    </Card>
  );
}
